import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete
} from '@mui/material';
import axios from 'axios';
import { API_BASE_URL } from 'src/apiConfig';
import { useTranslation } from 'react-i18next';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { ListboxComponent } from '../components/helperFunction';

/* DIALOG ZA DODAVANJE USERA */

const AddUserModal = ({ open, onClose, onSave, roles }) => {
  const { t } = useTranslation();
  const [userName, setUserName] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState(null); 
  const [schools, setSchools] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState('');
  const [notes, setNotes] = useState('');

  // Stanja za greške
  const [userNameError, setUserNameError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [roleError, setRoleError] = useState('');
  const [schoolError, setSchoolError] = useState('');

  useEffect(() => {
    const fetchSchoolsWithoutAccounts = async () => {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/schools.php?schoolsWithoutAccounts`
        );
        setSchools(response.data);
      } catch (error) {
        console.error('Error fetching schools:', error);
      }
    };

    fetchSchoolsWithoutAccounts(); 
  }, []);

  // Funkcija za generisanje lozinke
  const generatePassword = () => {
    const chars =
      'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789.!?';
    let generatedPassword = '';
    for (let i = 0; i < 8; i++) {
      generatedPassword += chars[Math.floor(Math.random() * chars.length)];
    }
    setPassword(generatedPassword);
  };

  // Funkcija za kreiranje korisničkog imena kada se izabere škola
  const handleSchoolChange = (event, newValue) => {
    if (newValue) {
      const schoolId = newValue.id;
      setSelectedSchool(schoolId);
      setUserName(`skola${schoolId}`);
      setSchoolError('');
    } else {
      setSelectedSchool('');
      setUserName('');
    }
  };

  // Funkcija za validaciju
  const validate = () => {
    let isValid = true;

    if (!userName.trim()) {
      setUserNameError(t('errors.required'));
      isValid = false;
    } else {
      setUserNameError('');
    }

    if (!password) {
      setPasswordError(t('errors.required'));
      isValid = false;
    } else if (password.length < 5) {
      setPasswordError(t('errors.passwordLength'));
      isValid = false;
    } else {
      setPasswordError('');
    }

    if (!role) {
      setRoleError(t('errors.required'));
      isValid = false;
    } else {
      setRoleError('');
    }

    if (role?.role_name === 'skola' && !selectedSchool) {
      setSchoolError(t('errors.required'));
      isValid = false;
    } else {
      setSchoolError('');
    }

    return isValid;
  };

  // Funkcija za kreiranje korisnika
  const handleSave = async () => {
    if (validate()) {
      const newUser = {
        userName: userName,
        password: password,
        role: role.id, 
        userRemark: notes,
        schoolID: selectedSchool
      };
      const success = await onSave(newUser); 
      if (success) {
        handleClose(); 
      }
    }
  };

  // Funkcija za resetovanje podataka prilikom zatvaranja
  const handleClose = () => {
    setUserName('');
    setPassword('');
    setRole(null);
    setSelectedSchool('');
    setNotes('');
    setUserNameError('');
    setPasswordError('');
    setRoleError('');
    setSchoolError('');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose} 
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: '12px',
          padding: '15px',
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)'
        }
      }}
    >
      <DialogTitle
        sx={{ fontSize: '1.2rem', fontWeight: 500, padding: '10px 20px' }}
      >
        {t('dialog.addUser')}
      </DialogTitle>
      <DialogContent sx={{ padding: '10px 20px' }}>
        <TextField
          label={t('dialog.username')}
          onChange={(e) => setUserName(e.target.value)} 
          fullWidth
          value={userName}
          disabled={role?.role_name === 'skola'}
          margin="dense"
          sx={{
            marginBottom: '5px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px'
          }}
          error={Boolean(userNameError)}
          helperText={userNameError}
        />

        <Box display="flex" alignItems="center">
          <TextField
            label={t('dialog.password')}
            fullWidth
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            margin="dense"
            sx={{
              marginBottom: '5px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px'
            }}
            error={Boolean(passwordError)}
            helperText={passwordError}
          />

          <Button
            onClick={generatePassword}
            sx={{
              marginLeft: '10px',
              backgroundColor: '#25be6c', 
              color: '#fff',
              textTransform: 'none',
              borderRadius: '8px',
              padding: '5px', 
              height: 'fit-content',
              minWidth: 'auto', 
              '&:hover': {
                backgroundColor: '#1e9e55' 
              }
            }}
          >
            <AutorenewIcon /> 
          </Button>
        </Box>

        <Autocomplete
          options={roles}
          getOptionLabel={(option) =>
            option ? t(`roles.${option.role_name}`) : ''
          }
          value={role}
          noOptionsText={t('pagination.noRowsLabel')}
          onChange={async (event, newValue) => {
            setRole(newValue);
            if (newValue?.role_name === 'skola') {
              setUserName('');
              setSelectedSchool('');
            } else {
              setSelectedSchool('');
            }
          }}
          isOptionEqualToValue={(option, value) => option.id === value.id} 
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('dialog.role')}
              margin="dense"
              fullWidth
              sx={{
                marginBottom: '5px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px'
              }}
              error={Boolean(roleError)}
              helperText={roleError}
            />
          )}
        />

        {role?.role_name === 'skola' && (
          <Autocomplete
            ListboxComponent={ListboxComponent}
            options={schools}
            noOptionsText={t('pagination.noRowsLabel')}
            getOptionLabel={(option) => option.name}
            onChange={handleSchoolChange}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('dialog.school')}
                margin="dense"
                fullWidth
                sx={{
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  marginBottom: '10px'
                }}
                error={Boolean(schoolError)}
                helperText={schoolError}
              />
            )}
          />
        )}

        <TextField
          label={t('dialog.notes')}
          fullWidth
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          margin="dense"
          multiline
          rows={3}
          sx={{
            marginBottom: '10px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px'
          }}
        />
      </DialogContent>
      <DialogActions sx={{ padding: '10px 20px' }}>
        <Button
          onClick={handleClose} 
          sx={{
            backgroundColor: '#f5f5f5',
            color: '#333',
            textTransform: 'none',
            fontWeight: 500,
            borderRadius: '8px',
            padding: '10px 20px',
            marginRight: '10px'
          }}
        >
          {t('dialog.cancel')}
        </Button>
        <Button
          onClick={handleSave} 
          sx={{
            backgroundColor: '#25be6c',
            color: '#fff',
            textTransform: 'none',
            fontWeight: 500,
            borderRadius: '8px',
            padding: '10px 20px'
          }}
        >
          {t('dialog.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddUserModal;
